import { useState, useEffect, useRef } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Alert,
  AlertIcon,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import { Bar } from "react-chartjs-2"
import ChartJS from "../../../tools/ChartSetup"
import axios from "axios"
import ExcelJS from "exceljs"

const DAY_COLORS = {
  Mon: "rgba(88, 182, 192, 0.8)",
  Tue: "rgba(88, 192, 135, 0.8)",
  Wed: "rgba(88, 182, 192, 0.6)",
  Thu: "rgba(88, 192, 135, 0.6)",
  Fri: "rgba(88, 182, 192, 0.45)",
  Sat: "rgba(255, 159, 64, 0.7)",
  Sun: "rgba(255, 99, 132, 0.6)",
}

// Detect data granularity from the gaps between data points
const detectGranularity = (data) => {
  if (data.length < 2) return "unknown"
  const gaps = []
  for (let i = 1; i < Math.min(data.length, 10); i++) {
    const diff = (new Date(data[i].day) - new Date(data[i - 1].day)) / (1000 * 60 * 60 * 24)
    gaps.push(Math.round(diff))
  }
  const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length
  if (avgGap <= 2) return "daily"
  if (avgGap <= 10) return "weekly"
  return "monthly"
}

const PERIOD_OPTIONS = [
  { value: "1", label: "1 Month" },
  { value: "3", label: "3 Months" },
  { value: "6", label: "6 Months" },
  { value: "12", label: "12 Months" },
]

const DailySales = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [dailyData, setDailyData] = useState([])
  const [dowData, setDowData] = useState([])
  const [womData, setWomData] = useState([])
  const [periodMonths, setPeriodMonths] = useState("3")
  const [downloading, setDownloading] = useState(false)
  const [granularity, setGranularity] = useState("daily")
  const chartRef = useRef(null)

  const r = retailer?.toUpperCase()
  const isWeeklyRetailer = r === "CHK" || r === "MED" || r === "PET"

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer, periodMonths])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const months = parseInt(periodMonths)
      const endDate = new Date()
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - months, 1)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

      const body = {
        supplier,
        retailer,
        userid: localStorage.getItem("userid"),
        startDate: fmt(startDate),
        endDate: fmt(endDate),
      }

      const patternEndpoint = isWeeklyRetailer
        ? "/insights/daily-sales/week-of-month"
        : "/insights/daily-sales/day-of-week"

      const [dailyRes, patternRes] = await Promise.all([
        axios.post(`${process.env.REACT_APP_API_URL}/insights/daily-sales`, body, { signal }),
        axios.post(`${process.env.REACT_APP_API_URL}${patternEndpoint}`, body, { signal }),
      ])

      if (dailyRes.data.status === 1) {
        setDailyData(dailyRes.data.data)
        setGranularity(detectGranularity(dailyRes.data.data))
      } else {
        setDailyData([])
      }

      if (patternRes.data.status === 1) {
        if (isWeeklyRetailer) {
          setWomData(patternRes.data.data)
          setDowData([])
        } else {
          setDowData(patternRes.data.data)
          setWomData([])
        }
      } else {
        setDowData([])
        setWomData([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("DailySales fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const formatShort = (val) => {
    const num = parseFloat(val) || 0
    if (num >= 1000000) return `R${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `R${(num / 1000).toFixed(0)}K`
    return `R${num.toFixed(0)}`
  }

  const granLabel = granularity === "daily" ? "day" : granularity === "weekly" ? "week" : "month"
  const granLabelPlural = granularity === "daily" ? "days" : granularity === "weekly" ? "weeks" : "months"

  // KPIs
  const totalUnits = dailyData.reduce((sum, d) => sum + parseInt(d.total_units || 0), 0)
  const totalAmount = dailyData.reduce((sum, d) => sum + parseFloat(d.total_amount || 0), 0)
  const avgUnits = dailyData.length > 0 ? Math.round(totalUnits / dailyData.length) : 0
  const bestEntry = dailyData.length > 0
    ? dailyData.reduce((best, d) => (parseInt(d.total_units) > parseInt(best.total_units) ? d : best), dailyData[0])
    : null

  // Format chart label based on granularity
  const formatLabel = (d) => {
    const date = new Date(d.day)
    if (granularity === "monthly") {
      return date.toLocaleDateString("en-ZA", { month: "short", year: "2-digit" })
    }
    if (granularity === "weekly") {
      return date.toLocaleDateString("en-ZA", { day: "numeric", month: "short" })
    }
    return `${date.getDate()} ${d.day_of_week}`
  }

  // Chart data
  const dailyChartData = {
    labels: dailyData.map(formatLabel),
    datasets: [
      {
        label: "Units Sold",
        data: dailyData.map((d) => parseInt(d.total_units)),
        backgroundColor: granularity === "daily"
          ? dailyData.map((d) => DAY_COLORS[d.day_of_week] || "rgba(88, 182, 192, 0.6)")
          : "rgba(88, 182, 192, 0.8)",
        borderRadius: 3,
      },
    ],
  }

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: (ctx) => {
            const row = dailyData[ctx.dataIndex]
            return `Amount: ${formatShort(row?.total_amount)}`
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Units" } },
      x: { ticks: { font: { size: 10 }, maxRotation: 45 } },
    },
  }

  // Day-of-week chart (only meaningful for daily data)
  const showDow = granularity === "daily" && dowData.length > 0
  const dowChartData = {
    labels: dowData.map((d) => d.day_of_week),
    datasets: [
      {
        label: "Avg Units",
        data: dowData.map((d) => parseInt(d.avg_units)),
        backgroundColor: dowData.map((d) => DAY_COLORS[d.day_of_week] || "rgba(88, 182, 192, 0.6)"),
        borderRadius: 4,
      },
    ],
  }

  const dowChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        color: "#6b7280",
        font: { size: 10, weight: "bold" },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Avg Units" } },
    },
  }

  // Week-of-month chart (only meaningful for weekly-granularity retailers)
  const showWom = granularity === "weekly" && womData.length > 0
  const womChartData = {
    labels: womData.map((d) => `Week ${d.week_of_month}`),
    datasets: [
      {
        label: "Avg Units",
        data: womData.map((d) => parseInt(d.avg_units)),
        backgroundColor: "rgba(88, 182, 192, 0.75)",
        borderRadius: 4,
      },
    ],
  }

  const womChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        color: "#6b7280",
        font: { size: 10, weight: "bold" },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Avg Units" } },
    },
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()

      if (dailyData.length > 0) {
        const sheetName = granularity === "daily" ? "Daily Sales" : granularity === "weekly" ? "Weekly Sales" : "Monthly Sales"
        const ws1 = workbook.addWorksheet(sheetName)
        ws1.columns = [
          { header: "Date", key: "day", width: 14 },
          { header: "Day", key: "day_of_week", width: 8 },
          { header: "Units", key: "total_units", width: 12 },
          { header: "Amount", key: "total_amount", width: 15 },
        ]
        dailyData.forEach((row) => {
          ws1.addRow({
            day: new Date(row.day).toLocaleDateString("en-ZA"),
            day_of_week: row.day_of_week,
            total_units: parseInt(row.total_units) || 0,
            total_amount: parseFloat(row.total_amount) || 0,
          })
        })
        ws1.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws1.getColumn("total_amount").numFmt = '#,##0.00'
      }

      if (showDow && dowData.length > 0) {
        const ws2 = workbook.addWorksheet("Day-of-Week Avg")
        ws2.columns = [
          { header: "Day", key: "day_of_week", width: 10 },
          { header: "# Days", key: "day_count", width: 8 },
          { header: "Avg Units", key: "avg_units", width: 12 },
          { header: "Avg Amount", key: "avg_amount", width: 15 },
          { header: "Total Units", key: "total_units", width: 12 },
          { header: "Total Amount", key: "total_amount", width: 15 },
        ]
        dowData.forEach((row) => {
          ws2.addRow({
            day_of_week: row.day_of_week,
            day_count: parseInt(row.day_count) || 0,
            avg_units: parseInt(row.avg_units) || 0,
            avg_amount: parseFloat(row.avg_amount) || 0,
            total_units: parseInt(row.total_units) || 0,
            total_amount: parseFloat(row.total_amount) || 0,
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws2.getColumn("avg_amount").numFmt = '#,##0.00'
        ws2.getColumn("total_amount").numFmt = '#,##0.00'
      }

      if (showWom && womData.length > 0) {
        const ws3 = workbook.addWorksheet("Week-of-Month Avg")
        ws3.columns = [
          { header: "Week of Month", key: "week_of_month", width: 14 },
          { header: "# Weeks", key: "week_count", width: 10 },
          { header: "Avg Units", key: "avg_units", width: 12 },
          { header: "Avg Amount", key: "avg_amount", width: 15 },
          { header: "Total Units", key: "total_units", width: 12 },
          { header: "Total Amount", key: "total_amount", width: 15 },
        ]
        womData.forEach((row) => {
          ws3.addRow({
            week_of_month: `Week ${row.week_of_month}`,
            week_count: parseInt(row.week_count) || 0,
            avg_units: parseInt(row.avg_units) || 0,
            avg_amount: parseFloat(row.avg_amount) || 0,
            total_units: parseInt(row.total_units) || 0,
            total_amount: parseFloat(row.total_amount) || 0,
          })
        })
        ws3.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws3.getColumn("avg_amount").numFmt = '#,##0.00'
        ws3.getColumn("total_amount").numFmt = '#,##0.00'
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Sales_${supplier}_${retailer}_${periodMonths}m.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  // Chart/section titles based on granularity
  const volumeTitle = granularity === "daily" ? "Daily Sales Volume" : granularity === "weekly" ? "Weekly Sales Volume" : "Monthly Sales Volume"
  const volumeDesc = granularity === "daily"
    ? "Day-by-day unit sales colour-coded by weekday to spot weekly demand patterns."
    : granularity === "weekly"
    ? "Weekly aggregated unit sales. This retailer reports data weekly, not daily."
    : "Monthly aggregated unit sales. This retailer reports data monthly, not daily."

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            Period
          </Text>
          <Select
            value={periodMonths}
            onChange={(e) => setPeriodMonths(e.target.value)}
            size="sm"
            w="160px"
            bg={cardBg}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Box>
        {dailyData.length > 0 && (
          <Button
            size="sm"
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Downloading"
          >
            Download Report
          </Button>
        )}
      </Flex>

      {/* Granularity notice for non-daily retailers */}
      {!loading && dailyData.length > 0 && granularity === "weekly" && (
        <Alert status="info" mb={4} borderRadius="md" fontSize="sm">
          <AlertIcon />
          This retailer reports sales weekly — not daily. Pattern analysis is shown by week-of-month instead of day-of-week.
        </Alert>
      )}
      {!loading && dailyData.length > 0 && granularity === "monthly" && (
        <Alert status="info" mb={4} borderRadius="md" fontSize="sm">
          <AlertIcon />
          This retailer reports sales monthly — not daily. Day-of-week and week-of-month analysis is not available.
        </Alert>
      )}

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : dailyData.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No sales data found for this period</Text>
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
            <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
              <StatLabel>Total Units</StatLabel>
              <StatNumber fontSize="xl">{totalUnits.toLocaleString()}</StatNumber>
              <StatHelpText>{dailyData.length} {granLabelPlural}</StatHelpText>
            </Stat>
            <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
              <StatLabel>Total Revenue</StatLabel>
              <StatNumber fontSize="xl">{formatShort(totalAmount)}</StatNumber>
              <StatHelpText>{formatCurrency(totalAmount)}</StatHelpText>
            </Stat>
            <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
              <StatLabel>Avg per {granLabel}</StatLabel>
              <StatNumber fontSize="xl">{avgUnits.toLocaleString()}</StatNumber>
              <StatHelpText>Units per {granLabel}</StatHelpText>
            </Stat>
            {bestEntry && (
              <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <StatLabel>Best {granLabel}</StatLabel>
                <StatNumber fontSize="xl">
                  {new Date(bestEntry.day).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}
                </StatNumber>
                <StatHelpText>
                  {parseInt(bestEntry.total_units).toLocaleString()} units
                </StatHelpText>
              </Stat>
            )}
          </SimpleGrid>

          {/* Sales Volume Chart */}
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={4} mb={4}>
            <Heading size="sm" mb={1}>
              {volumeTitle}
            </Heading>
            <Text fontSize="xs" color="gray.500" mb={3}>
              {volumeDesc}
            </Text>
            <Box height="300px">
              <Bar ref={chartRef} data={dailyChartData} options={dailyChartOptions} />
            </Box>
          </Box>

          {/* Week-of-Month Average Chart + Table (weekly granularity only) */}
          {showWom && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={4}>
              <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={4}>
                <Heading size="sm" mb={1}>
                  Average Sales by Week of Month
                </Heading>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Which week of the month sells most. Useful for timing replenishment around pay-cycle weeks.
                </Text>
                <Box height="250px">
                  <Bar data={womChartData} options={womChartOptions} />
                </Box>
              </Box>

              <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <Box p={3} borderBottom="1px" borderColor={borderColor}>
                  <Heading size="sm">Week-of-Month Breakdown</Heading>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Totals and averages grouped by week-of-month position.
                  </Text>
                </Box>
                <Box maxH="280px" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Week</Th>
                        <Th isNumeric># Weeks</Th>
                        <Th isNumeric>Avg Units</Th>
                        <Th isNumeric>Avg Amount</Th>
                        <Th isNumeric>Total Units</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {womData.map((row, i) => (
                        <Tr key={i} _hover={{ bg: hoverBg }}>
                          <Td fontSize="xs" fontWeight="bold">
                            Week {row.week_of_month}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {row.week_count}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {parseInt(row.avg_units).toLocaleString()}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {formatCurrency(row.avg_amount)}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold">
                            {parseInt(row.total_units).toLocaleString()}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            </SimpleGrid>
          )}

          {/* Day-of-Week Average Chart + Table (daily granularity only) */}
          {showDow && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={4}>
                <Heading size="sm" mb={1}>
                  Average Sales by Weekday
                </Heading>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Which days of the week sell most. Use this to time replenishment and schedule rep visits on high-volume days.
                </Text>
                <Box height="250px">
                  <Bar data={dowChartData} options={dowChartOptions} />
                </Box>
              </Box>

              <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <Box p={3} borderBottom="1px" borderColor={borderColor}>
                  <Heading size="sm">Weekday Breakdown</Heading>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Totals and averages per day of week for the selected period.
                  </Text>
                </Box>
                <Box maxH="280px" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Day</Th>
                        <Th isNumeric># Days</Th>
                        <Th isNumeric>Avg Units</Th>
                        <Th isNumeric>Avg Amount</Th>
                        <Th isNumeric>Total Units</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dowData.map((row, i) => (
                        <Tr key={i} _hover={{ bg: hoverBg }}>
                          <Td fontSize="xs" fontWeight="bold">
                            {row.day_of_week}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {row.day_count}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {parseInt(row.avg_units).toLocaleString()}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {formatCurrency(row.avg_amount)}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold">
                            {parseInt(row.total_units).toLocaleString()}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            </SimpleGrid>
          )}
        </>
      )}
    </Box>
  )
}

export default DailySales
